import { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { useAuth } from '@shared/context/useAuth';
import type { TaskRequestFilters, TaskStatus, TaskType, TaskPriority } from '../types/taskRequest';

interface TaskFiltersProps {
  filters: TaskRequestFilters;
  onFiltersChange: (filters: TaskRequestFilters) => void;
}

export default function TaskFilters({ filters, onFiltersChange }: Readonly<TaskFiltersProps>) {
  const { user } = useAuth();
  const [searchInput, setSearchInput] = useState(filters.search ?? '');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search input
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      if (searchInput !== (filters.search ?? '')) {
        onFiltersChange({ ...filters, search: searchInput || undefined, page: 1 });
      }
    }, 400);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasActiveFilters =
    filters.status !== undefined ||
    filters.taskType !== undefined ||
    filters.priority !== undefined ||
    filters.assignedToUserId !== undefined ||
    (filters.search && filters.search.length > 0);

  // Derive the assignment filter value from the current filters
  const assignmentValue =
    filters.assignedToUserId === undefined
      ? ''
      : filters.assignedToUserId === user?.userId
        ? 'me'
        : String(filters.assignedToUserId);

  const handleClearFilters = () => {
    setSearchInput('');
    onFiltersChange({
      page: 1,
      pageSize: filters.pageSize,
      sortColumn: filters.sortColumn,
      sortDirection: filters.sortDirection,
      assignedToUserId: undefined,
    });
  };

  return (
    <div className="task-filters">
      <div className="task-filters-search-row">
        <div className="task-filter-search">
          <Search size={16} className="task-filter-search-icon" />
          <input
            type="text"
            placeholder="Search tasks..."
            aria-label="Search tasks"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="task-filter-search-input"
          />
          {searchInput && (
            <button
              type="button"
              className="task-filter-search-clear"
              onClick={() => setSearchInput('')}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <select
          className="task-filter-select"
          value={assignmentValue}
          onChange={(e) => {
            const val = e.target.value;
            let userId: number | undefined;
            if (val === 'me' && user) {
              userId = user.userId;
            } else if (val) {
              userId = Number(val);
            }
            onFiltersChange({
              ...filters,
              assignedToUserId: userId,
              page: 1,
            });
          }}
          aria-label="Filter by assignment"
        >
          <option value="">All Assignments</option>
          <option value="me">Assigned to Me</option>
        </select>
      </div>

      <div className="task-filters-row">
        <select
          className="task-filter-select"
          value={filters.status ?? ''}
          onChange={(e) =>
            onFiltersChange({
              ...filters,
              status: (e.target.value as TaskStatus) || undefined,
              page: 1,
            })
          }
          aria-label="Filter by status"
        >
          <option value="">All Statuses</option>
          <option value="New">New</option>
          <option value="InProgress">In Progress</option>
          <option value="Completed">Completed</option>
          <option value="Rejected">Rejected</option>
        </select>

        <select
          className="task-filter-select"
          value={filters.taskType ?? ''}
          onChange={(e) =>
            onFiltersChange({
              ...filters,
              taskType: (e.target.value as TaskType) || undefined,
              page: 1,
            })
          }
          aria-label="Filter by type"
        >
          <option value="">All Types</option>
          <option value="SetupNewClinic">Setup New Clinic</option>
          <option value="ImAddClinic">IM - Add Clinic</option>
          <option value="CreateClinicRelationship">Create Clinic Relationship</option>
        </select>

        <select
          className="task-filter-select"
          value={filters.priority !== undefined ? String(filters.priority) : ''}
          onChange={(e) =>
            onFiltersChange({
              ...filters,
              priority: e.target.value ? (Number(e.target.value) as TaskPriority) : undefined,
              page: 1,
            })
          }
          aria-label="Filter by priority"
        >
          <option value="">All Priorities</option>
          <option value="1">Low</option>
          <option value="2">Normal</option>
          <option value="3">High</option>
          <option value="4">Urgent</option>
        </select>

        {hasActiveFilters && (
          <button
            type="button"
            className="task-filter-clear-btn"
            onClick={handleClearFilters}
          >
            <X size={14} />
            Clear Filters
          </button>
        )}
      </div>
    </div>
  );
}
